import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';

import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import axios from '@/plugins/axios.js';

const PENDING_GENERATION_STATUSES = ['pending', 'queued', 'processing', 'generating'];
const POLL_INTERVAL_MS = 15000;

/**
 * Slide-deck-specific behaviour for the activity page: fetch the linked slide deck
 * and poll while it is still generating. This mirrors the inline logic in the
 * (frozen) stanbridge `SlideDeck.vue` so the corporate variant can have it without
 * editing the frozen renderer. When stanbridge is eventually un-frozen, it should
 * adopt this composable and the duplicate inline copy can be deleted.
 *
 * Globals are read lazily off the component proxy ($store), mirroring useActivity.
 *
 * @param {object} props - reactive props with `namespace` and `uuid`
 */
export function useSlideDeckActivity(props) {
    const instance = getCurrentInstance();

    if (!instance) {
        throw new Error('useSlideDeckActivity() must be called inside setup().');
    }

    const { proxy } = instance;

    const slideDeck = ref(null);
    const isLoadingDeck = ref(true);
    const generationStatus = ref(null);
    const generationError = ref(null);
    let statusPollIntervalId = null;
    let consecutiveFailures = 0;

    // Give up after repeated failures so a dead/5xx endpoint doesn't poll forever
    // (mirrors useCourseImageRegenerationPolling).
    const MAX_CONSECUTIVE_FAILURES = 5;

    const slideDeckId = computed(
        () => proxy.$store.getters[`${props.namespace}/${props.uuid}/get`]('activity_content')?.slide_deck_id
    );

    const isGenerating = computed(() => {
        if (PENDING_GENERATION_STATUSES.includes(generationStatus.value)) return true;

        return (
            !!generationStatus.value && generationStatus.value !== 'failed' && !slideDeck.value && !slideDeckId.value
        );
    });

    const isFailedGeneration = computed(() => generationStatus.value === 'failed');

    const showSlideDeckSkeleton = computed(
        () => isLoadingDeck.value && !isGenerating.value && !isFailedGeneration.value && !slideDeck.value
    );

    const clearStatusPolling = () => {
        if (statusPollIntervalId) {
            clearInterval(statusPollIntervalId);
            statusPollIntervalId = null;
        }
    };

    const ensureStatusPolling = () => {
        if (!isGenerating.value) {
            clearStatusPolling();
            return;
        }

        if (statusPollIntervalId) return;

        statusPollIntervalId = setInterval(() => {
            loadActivity().catch(() => {});
        }, POLL_INTERVAL_MS);
    };

    const loadActivity = async () => {
        try {
            const { id } = proxy.$store.getters[`${props.namespace}/get`](props.uuid);
            const endpoint = props.namespace === 'Activities' ? 'activities' : 'activity-templates';

            const { data } = await axios.get(`/api/${endpoint}/${id}?include=contentable.slideDeck`);

            generationStatus.value = data.data.activity_content?.generation_status ?? null;
            generationError.value = data.data.activity_content?.generation_error ?? null;

            const deckData = data.data.activity_content?.slide_deck;
            if (deckData?.reference_id) {
                slideDeck.value = await proxy.$store.dispatch('SlideDecks/fetchOne', deckData.reference_id);
            }

            consecutiveFailures = 0;
        } catch (error) {
            consecutiveFailures += 1;

            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                // Stop retrying a persistently failing endpoint: surface a failed state so the UI
                // shows an error instead of a perpetual "generating" spinner, and polling clears
                // (isGenerating becomes false).
                generationStatus.value = 'failed';
            } else if (generationStatus.value == null) {
                // With no known status yet, treat a transient first-load failure as still-pending so
                // isGenerating stays true and ensureStatusPolling() arms the retry interval — otherwise
                // the UI would go silently blank (no skeleton, no generating/failure indicator, no retry).
                generationStatus.value = 'pending';
            }
            throw error;
        } finally {
            // Always clear the skeleton and (re)arm polling — a transient first-load failure must not
            // leave the component stuck on the loading skeleton with no retry.
            isLoadingDeck.value = false;
            ensureStatusPolling();
        }
    };

    onMounted(() => {
        loadActivity().catch(() => {});
    });

    onUnmounted(clearStatusPolling);

    return {
        slideDeck,
        isLoadingDeck,
        generationStatus,
        generationError,
        isGenerating,
        isFailedGeneration,
        showSlideDeckSkeleton,
        isGenerationStatusUiEnabled: ACTIVITY_GENERATION_STATUS_UI_ENABLED,
        loadActivity,
    };
}
